import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import * as apps from '../api/apps';
import { useSession } from '../state/useSession';
import { useProjects } from '../state/projects';
import { strings } from '@/theme';
import {
  liveAppRows,
  liveElsewhereRows,
  siteStatusesFrom,
  liveRowLabel,
  MY_APPS_COPY,
  type OwnListing,
} from '@/utils/myApps';
import { EmptyState, Screen } from '../ui';
import ProjectCard from './ProjectCard';

/**
 * Ported from src/app/(main)/(tabs)/my-apps.tsx — the signed-in person's own apps that finished
 * building and are live, by the same shared rule (`liveAppRows`), in the same words.
 *
 * It was the catalogue's list of apps this person had published or JOINED, each opening a
 * listing page rather than the editor, and its empty state sent people to Discover. Rows now come
 * from the person's own projects, each opening its editor; then the person's live apps made on
 * another device (`liveElsewhereRows`), which have no project in this browser and open their page.
 */
export default function MyApps() {
  const navigate = useNavigate();
  const { user } = useSession();
  const projects = useProjects((s) => s.projects);
  const isLoading = useProjects((s) => s.isLoading);
  const error = useProjects((s) => s.error);
  // What the server last said about this person's apps; null until it has answered once.
  const [listings, setListings] = useState<OwnListing[] | null>(null);

  useEffect(() => {
    void useProjects.getState().loadProjects(user?.id ?? '');
  }, [user?.id]);

  /*
   * What the server says about each page. For a project it only ever REMOVES a row it says is
   * offline; an answer that did not come leaves every project row standing on its record, because
   * a failed lookup is a fact about the moment, not about somebody's site — and lists nothing from
   * another device, where the server's word is the only one there is.
   */
  useEffect(() => {
    let live = true;
    void apps
      .fetchMyApps()
      .then((answer) => {
        if (live) setListings(answer);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user?.id]);

  const rows = useMemo(() => liveAppRows(projects, siteStatusesFrom(listings)), [projects, listings]);
  const elsewhere = useMemo(() => liveElsewhereRows(listings, projects, user?.id), [listings, projects, user?.id]);

  if (rows.length === 0 && elsewhere.length === 0) {
    return (
      <Screen title={strings.tabs.myApps}>
        {isLoading ? (
          <EmptyState icon="emptyApps" title={MY_APPS_COPY.loading} body="" />
        ) : error ? (
          <EmptyState icon="emptyApps" title={MY_APPS_COPY.errorTitle} body={MY_APPS_COPY.errorBody} />
        ) : (
          <EmptyState
            icon="emptyApps"
            title={MY_APPS_COPY.emptyTitle}
            body={MY_APPS_COPY.emptyBody}
            ctaTitle={MY_APPS_COPY.create}
            onCta={() => navigate('/new/idea')}
          />
        )}
      </Screen>
    );
  }

  return (
    <Screen title={strings.tabs.myApps}>
      <ul className="project-list app-grid">
        {rows.map(({ project, url, address, badge }) => (
          <ProjectCard
            key={project.id}
            name={project.name}
            onOpenEditor={() => navigate(`/project/${project.id}`)}
            label={liveRowLabel({ name: project.name, address, badge })}
            href={url}
            address={address}
            meta={badge}
          />
        ))}
        {/* No project for these in this browser, so no editor: the whole card is one link to the
            page, and says why — rather than a row whose click does something else than its twin. */}
        {elsewhere.map(({ appId, name, url, address, badge }) => (
          <ProjectCard
            key={`a:${appId}`}
            name={name}
            href={url}
            label={`Open ${name} in a new tab`}
            address={address}
            meta={badge}
            note={MY_APPS_COPY.elsewhere}
          />
        ))}
      </ul>
    </Screen>
  );
}
